import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import type {
  Depth,
  DiscussionOutput,
  DiscussionTopic,
  DiscussionUserResponse,
} from "@/api/create-types";
import { Alert, Button } from "@/components/ui";
import type { RendererProps } from "./types";

const DEPTHS: { value: Depth; label: string }[] = [
  { value: "already_know", label: "Know it" },
  { value: "recap_briefly", label: "Recap" },
  { value: "dig_deeper", label: "Deeper" },
  { value: "from_scratch", label: "From scratch" },
];

const bulkKey = (topicId: string, idx: number) => `${topicId}::${idx}`;

/** Renderer for `headteacher.discussion` — the unified multi-agent planner.
 *  Full functional parity with iOS DiscussionReviewView (3-slide checkpoint),
 *  presented as three stacked sections on one scrollable page. The submitted
 *  user_response must satisfy the strict (`extra="forbid"`) DiscussionUserResponse
 *  schema, so we mirror the iOS submit() logic exactly. */
export function DiscussionReview({ step, submitting, error, onAdvance }: RendererProps) {
  const { t } = useTranslation();
  const output = (step.output ?? {}) as unknown as DiscussionOutput;
  const topics = output.topics ?? [];
  const infoCards = output.info_cards ?? [];
  const uncertainties = output.uncertainties ?? [];
  const historyProposals = output.history_proposals ?? [];
  const summaryLines = (output.summary_blocks ?? [])
    .map((b) => b.text)
    .filter((s) => !!s);
  const goals = output.goals ?? [];
  const discussionNotes = output.discussion_notes ?? [];

  const plannedTotal = useMemo(
    () => topics.reduce((sum, tp) => sum + (tp.question_count || 0), 0),
    [topics],
  );

  // State (mirrors iOS decode()).
  const [keepTopic, setKeepTopic] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(topics.map((tp) => [tp.id, true])),
  );
  const [depthOverride, setDepthOverride] = useState<Record<string, Depth>>(() =>
    Object.fromEntries(topics.map((tp) => [tp.id, tp.depth])),
  );
  const [acceptHistory, setAcceptHistory] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(historyProposals.map((h) => [h.id, !!h.default])),
  );
  const [uncertaintyChoice, setUncertaintyChoice] = useState<Record<string, string>>(() =>
    Object.fromEntries(uncertainties.filter((u) => u.default).map((u) => [u.id, u.default])),
  );
  const [keepCard, setKeepCard] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(infoCards.map((c) => [c.id, c.selected_default])),
  );
  const [dropBulk, setDropBulk] = useState<Record<string, boolean>>({});
  const [emphasize, setEmphasize] = useState<Record<string, Set<string>>>({});
  const [trimTotal, setTrimTotal] = useState<number>(plannedTotal);
  const [expandedBrief, setExpandedBrief] = useState<Set<string>>(new Set());
  const [showTeacherNotes, setShowTeacherNotes] = useState(false);

  const keptTopics = topics.filter((tp) => keepTopic[tp.id] ?? true);
  const selectedCount = keptTopics.length;

  const toggleEmphasis = (topicId: string, qt: string) =>
    setEmphasize((s) => {
      const next = new Set(s[topicId] ?? []);
      if (next.has(qt)) next.delete(qt);
      else next.add(qt);
      return { ...s, [topicId]: next };
    });

  const submit = () => {
    const topic_decisions = topics.map((tp) => {
      const keep = keepTopic[tp.id] ?? true;
      const depth = depthOverride[tp.id];
      return {
        topic_id: tp.id,
        keep,
        ...(depth && depth !== tp.depth ? { depth } : {}),
      };
    });
    const info_card_decisions = infoCards.map((c) => ({
      info_card_id: c.id,
      selected: keepCard[c.id] ?? c.selected_default,
    }));
    const bulk_focus_decisions = topics
      .map((tp) => {
        const drops = (tp.ordered_bulks ?? [])
          .map((_b, idx) => (dropBulk[bulkKey(tp.id, idx)] ? idx : null))
          .filter((v): v is number => v !== null);
        const emph = Array.from(emphasize[tp.id] ?? []);
        return { topic_id: tp.id, drop_bulk_indices: drops, emphasize_question_types: emph };
      })
      .filter((d) => d.drop_bulk_indices.length > 0 || d.emphasize_question_types.length > 0);
    const history_decisions = historyProposals.map((h) => ({
      proposal_id: h.id,
      accept: acceptHistory[h.id] ?? !!h.default,
    }));
    const uncertainty_decisions = uncertainties
      .filter((u) => !!uncertaintyChoice[u.id])
      .map((u) => ({ uncertainty_id: u.id, choice: uncertaintyChoice[u.id] }));

    const payload: DiscussionUserResponse = {
      topic_decisions,
      info_card_decisions,
      bulk_focus_decisions,
      history_decisions,
      uncertainty_decisions,
      ...(trimTotal !== plannedTotal ? { trim_total_questions: trimTotal } : {}),
    };
    onAdvance(payload as unknown as Record<string, unknown>);
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-bold tracking-tight">
          {output.set_title?.trim() ? output.set_title : t("Eddi's plan")}
        </h2>
        <p className="mt-1 text-sm text-ink-faint">
          {t("Fine-tune the plan, then generate. Pick topics and depth, choose teaching cards, and shape the structure.")}
        </p>
      </div>

      {error && <Alert>{error}</Alert>}

      {/* ── Section 1 — Topics & depth ─────────────────────────────── */}
      <section className="space-y-4">
        <h3 className="text-sm font-bold uppercase tracking-wide text-ink-faint">
          {t("1 · Topics & depth")}
        </h3>

        {historyProposals.length > 0 && (
          <div className="rounded-2xl border border-amber-300/60 bg-amber-50 p-4">
            <p className="font-semibold text-amber-700">{t("Focus on past mistakes")}</p>
            <p className="mt-0.5 text-xs text-amber-700/80">
              {t("Eddi spotted topics you've slipped on before. Keep these on to re-drill them.")}
            </p>
            <div className="mt-3 space-y-2">
              {historyProposals.map((h) => (
                <label key={h.id} className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={acceptHistory[h.id] ?? !!h.default}
                    onChange={(e) => setAcceptHistory((s) => ({ ...s, [h.id]: e.target.checked }))}
                    className="mt-0.5 h-5 w-5 rounded border-amber-400 text-amber-600 focus:ring-amber-400"
                  />
                  <span className="flex-1">
                    <span className="block text-sm font-medium text-ink">{h.label}</span>
                    {h.rationale && (
                      <span className="mt-0.5 block text-xs text-ink-faint">{h.rationale}</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {summaryLines.length > 0 && (
          <div className="rounded-2xl bg-brand-50/60 p-4">
            <p className="text-sm font-semibold text-ink-soft">{t("The plan in brief")}</p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-ink-faint">
              {summaryLines.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
          </div>
        )}

        {goals.length > 0 && (
          <div className="card p-4">
            <p className="text-sm font-semibold text-ink-soft">{t("Goals")}</p>
            <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-ink-faint">
              {goals.map((g, i) => (
                <li key={i}>{g}</li>
              ))}
            </ul>
          </div>
        )}

        {uncertainties.length > 0 && (
          <div className="rounded-2xl bg-yellow-50 p-4">
            <p className="text-sm font-semibold text-ink-soft">{t("A few questions")}</p>
            <div className="mt-3 space-y-3">
              {uncertainties.map((u) => (
                <div key={u.id}>
                  <p className="text-sm text-ink">{u.question}</p>
                  <div className="mt-1.5 flex flex-wrap gap-1.5">
                    {u.options.map((opt) => {
                      const active = (uncertaintyChoice[u.id] ?? u.default) === opt;
                      return (
                        <button
                          key={opt}
                          type="button"
                          onClick={() => setUncertaintyChoice((s) => ({ ...s, [u.id]: opt }))}
                          className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                            active
                              ? "bg-brand-600 text-white"
                              : "bg-white text-ink-soft hover:bg-brand-50"
                          }`}
                        >
                          {opt}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {discussionNotes.length > 0 && (
          <div className="rounded-2xl bg-ink/[0.03] p-4">
            <button
              type="button"
              onClick={() => setShowTeacherNotes((v) => !v)}
              className="text-sm font-semibold text-brand-600 hover:underline"
            >
              {showTeacherNotes
                ? t("Hide the teachers' discussion")
                : t("See what the teachers discussed")}
            </button>
            {showTeacherNotes && (
              <div className="mt-3 space-y-3">
                {discussionNotes.map((note, ni) => (
                  <div key={ni} className="rounded-xl bg-white p-3">
                    <p className="text-sm font-bold text-ink">{note.role_label || note.role}</p>
                    {note.topic_notes.map((tn, ti) => (
                      <p key={ti} className="mt-1 text-xs text-ink-faint">
                        <span className="font-semibold">{tn.label}: </span>
                        {tn.why}
                        {tn.scope_flag !== "ok" && tn.scope_suggestion && (
                          <span className="text-amber-600"> ⚠ {tn.scope_suggestion}</span>
                        )}
                      </p>
                    ))}
                    {note.flags.map((f, fi) => (
                      <p key={fi} className="mt-1 text-xs text-ink-faint">⚑ {f}</p>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="space-y-3">
          <p className="text-sm font-semibold text-ink-soft">{t("Topics")}</p>
          {topics.map((tp) => {
            const kept = keepTopic[tp.id] ?? true;
            const open = expandedBrief.has(tp.id);
            const hasBrief = tp.key_points.length > 0 || tp.key_terms.length > 0;
            return (
              <div key={tp.id} className={`card p-4 transition ${kept ? "" : "opacity-60"}`}>
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={kept}
                    onChange={(e) => setKeepTopic((s) => ({ ...s, [tp.id]: e.target.checked }))}
                    className="mt-1 h-5 w-5 rounded border-black/20 text-brand-600 focus:ring-brand-400"
                  />
                  <span className="flex-1">
                    <span className="block font-semibold text-ink">{tp.label}</span>
                    {tp.summary && (
                      <span className="mt-0.5 block text-sm text-ink-faint">{tp.summary}</span>
                    )}
                  </span>
                </label>

                {kept && (
                  <div className="mt-3 flex flex-wrap gap-1.5 pl-8">
                    {DEPTHS.map((d) => {
                      const active = (depthOverride[tp.id] ?? tp.depth) === d.value;
                      return (
                        <button
                          key={d.value}
                          type="button"
                          onClick={() => setDepthOverride((s) => ({ ...s, [tp.id]: d.value }))}
                          className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                            active
                              ? "bg-brand-600 text-white"
                              : "bg-brand-50 text-brand-700 hover:bg-brand-100"
                          }`}
                        >
                          {t(d.label)}
                        </button>
                      );
                    })}
                  </div>
                )}

                {kept && hasBrief && (
                  <div className="mt-3 pl-8">
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedBrief((s) => {
                          const next = new Set(s);
                          if (next.has(tp.id)) next.delete(tp.id);
                          else next.add(tp.id);
                          return next;
                        })
                      }
                      className="text-xs font-semibold text-brand-600 hover:underline"
                    >
                      {open ? t("Hide what you'll learn") : t("What you'll learn")}
                    </button>
                    {open && (
                      <div className="mt-2 space-y-1 text-xs text-ink-faint">
                        {tp.key_points.map((p, pi) => (
                          <p key={pi}>• {p}</p>
                        ))}
                        {tp.key_terms.map((kt, ki) => (
                          <p key={ki}>
                            <span className="font-semibold">{kt.term}</span>
                            {kt.definition ? ` — ${kt.definition}` : ""}
                          </p>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* ── Section 2 — Info cards ─────────────────────────────────── */}
      <section className="space-y-3">
        <h3 className="text-sm font-bold uppercase tracking-wide text-ink-faint">
          {t("2 · Teaching cards")}
        </h3>
        <p className="text-sm text-ink-faint">
          {t("Eddi can prepare these teaching cards. Pick the ones you want.")}
        </p>
        {infoCards.length === 0 ? (
          <p className="text-sm text-ink-faint">{t("No info cards proposed for this set.")}</p>
        ) : (
          infoCards.map((card) => (
            <label key={card.id} className="card flex cursor-pointer items-start gap-3 p-4">
              <input
                type="checkbox"
                checked={keepCard[card.id] ?? card.selected_default}
                onChange={(e) => setKeepCard((s) => ({ ...s, [card.id]: e.target.checked }))}
                className="mt-1 h-5 w-5 rounded border-black/20 text-brand-600 focus:ring-brand-400"
              />
              <span className="flex-1">
                <span className="block font-semibold text-ink">{card.title}</span>
                {card.covers && (
                  <span className="mt-0.5 block text-sm text-ink-faint">{card.covers}</span>
                )}
                {card.why && (
                  <span className="mt-0.5 block text-xs text-ink-faint/80">{card.why}</span>
                )}
              </span>
            </label>
          ))
        )}
      </section>

      {/* ── Section 3 — Structure & length ─────────────────────────── */}
      <section className="space-y-4">
        <h3 className="text-sm font-bold uppercase tracking-wide text-ink-faint">
          {t("3 · Structure & length")}
        </h3>

        {plannedTotal > 0 && (
          <div className="card p-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-ink-soft">{t("Total length")}</span>
              <span className="text-sm text-ink">
                {t("{{count}} questions", { count: trimTotal })}
              </span>
            </div>
            <input
              type="range"
              min={Math.max(1, Math.floor(plannedTotal / 4))}
              max={Math.max(2, plannedTotal)}
              step={1}
              value={trimTotal}
              onChange={(e) => setTrimTotal(Number(e.target.value))}
              className="mt-2 w-full accent-brand-600"
            />
          </div>
        )}

        {keptTopics.map((tp: DiscussionTopic) => (
          <div key={tp.id} className="card p-4">
            <p className="font-semibold text-ink">{tp.label}</p>
            <div className="mt-2 space-y-3">
              {(tp.ordered_bulks ?? []).map((bulk, idx) => {
                const key = bulkKey(tp.id, idx);
                const dropped = dropBulk[key] ?? false;
                return (
                  <div key={idx} className={dropped ? "opacity-50" : ""}>
                    <label className="flex cursor-pointer items-center gap-2">
                      <input
                        type="checkbox"
                        checked={!dropped}
                        onChange={(e) => setDropBulk((s) => ({ ...s, [key]: !e.target.checked }))}
                        className="h-4 w-4 rounded border-black/20 text-brand-600 focus:ring-brand-400"
                      />
                      <span className="text-sm font-semibold text-ink">
                        {bulk.bulk_kind
                          ? bulk.bulk_kind.charAt(0).toUpperCase() + bulk.bulk_kind.slice(1)
                          : t("Practice")}
                      </span>
                      {bulk.focus && (
                        <span className="truncate text-xs text-ink-faint">· {bulk.focus}</span>
                      )}
                    </label>
                    {!dropped && bulk.question_types.length > 0 && (
                      <div className="mt-1.5 flex flex-wrap gap-1.5 pl-6">
                        {bulk.question_types.map((qt) => {
                          const on = emphasize[tp.id]?.has(qt) ?? false;
                          return (
                            <button
                              key={qt}
                              type="button"
                              onClick={() => toggleEmphasis(tp.id, qt)}
                              className={`rounded-full px-2.5 py-1 text-[11px] font-semibold transition ${
                                on
                                  ? "bg-brand-600 text-white"
                                  : "bg-ink/[0.06] text-ink-soft hover:bg-brand-50"
                              }`}
                            >
                              {qt.replace(/_/g, " ")}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </section>

      <div className="flex items-center gap-3 pt-1">
        <Button onClick={submit} size="lg" loading={submitting} disabled={selectedCount === 0}>
          {t("Generate {{count}} topics", { count: selectedCount })}
        </Button>
        {selectedCount === 0 && (
          <span className="text-sm text-ink-faint">{t("Select at least one topic.")}</span>
        )}
      </div>
    </div>
  );
}
